/**
 * useUploadFlow — Encapsulates Activity Bar upload flow state and handlers.
 *
 * Manages: upload mode (file/text), selected file, text content, upload status,
 * file input ref, cancel/begin/select handlers, OCR extraction, and v3 processing submission.
 *
 * Extracted from ActivityBar.js to reduce component size (~135 lines).
 * See: docs/features/infrastructure/app-shell.md#activity-bar
 */
import { useState, useRef, useCallback } from "react";
import { minimumTextLength, ServerURI } from "../globals";
import { submitForProcessing } from "../utils/processingService";
import { addData, updateData, getData } from "../utils/firestoreService";
import { createInitialDocumentStructure, getMimeType, withRetry } from "../utils/documentCreationUtils";
import { getSharedDocumentMetadata, canPerformAction } from "../utils/sharingService";
import { logAuditEventAsync, AuditActions } from "../utils/auditLogger";
import { showNotification } from "../utils/notificationSystem";
import { SESSION_STATE } from "../utils/sessionTimeout";
import { useActiveJobsContext } from "../contexts/ActiveJobsContext";
import { getAuthHeaders } from "../utils/apiUtils";

const UPLOAD_MODE_KEY = "fg-upload-mode";
const UPLOAD_TYPES = ["application/pdf", "image/tiff", "image/jpeg", "image/png", "image/webp"];
function getUploadMimeType(file) {
    if (!file) return null;
    return UPLOAD_TYPES.includes(file.type) ? file.type : getMimeType(file.name);
}
function getPersistedUploadMode() {
    try { return localStorage.getItem(UPLOAD_MODE_KEY) || "file"; } catch { return "file"; }
}
function persistUploadMode(mode) {
    try { localStorage.setItem(UPLOAD_MODE_KEY, mode); } catch { /* ignore */ }
}

/**
 * @param {Object} params
 * @param {string}   params.patientId
 * @param {string}   params.userId
 * @param {Object}   params.dropdownState
 * @param {Object}   params.session
 * @param {Function} params.setIsProcessing
 * @param {Function} params.onUploadDocCreated
 * @param {Function} params.addMessage
 * @param {string}   params.sessionState
 * @param {Function} params.extendSessionOnAction
 * @param {Function} params.setUploadStatus   - setter for upload status (state owned by caller to avoid circular deps with useActivityMessages)
 * @param {Function} params.onUploadComplete  - callback invoked when upload is successfully submitted for processing
 */
export function useUploadFlow({
    patientId,
    userId,
    dropdownState,
    session,
    setIsProcessing,
    onUploadDocCreated,
    addMessage,
    sessionState,
    extendSessionOnAction,
    setUploadStatus,
    onUploadComplete,
}) {
    const [showUploadUI, setShowUploadUI] = useState(false);
    const [uploadModeRaw, setUploadModeRaw] = useState(getPersistedUploadMode); // "file" | "text"
    const [selectedFile, setSelectedFile] = useState(null);
    const [uploadTextContent, setUploadTextContent] = useState("");
    const fileInputRef = useRef(null);
    const { addPendingJob, removePendingJob } = useActiveJobsContext();

    // Wrap setter to persist the user's preferred upload mode
    const setUploadMode = useCallback((mode) => {
        setUploadModeRaw(mode);
        persistUploadMode(mode);
    }, []);
    const uploadMode = uploadModeRaw;

    const handleCancelUpload = useCallback(() => {
        setShowUploadUI(false);
        // Keep uploadMode — remember user's last preference
        setSelectedFile(null);
        setUploadTextContent("");
        setUploadStatus("idle");
    }, [setUploadStatus]);

    const handleBeginUpload = useCallback(async () => {
        if (!patientId) return;

        // Block uploads during grace period
        if (sessionState === SESSION_STATE.GRACE || sessionState === SESSION_STATE.GRACE_URGENT || sessionState === SESSION_STATE.EXPIRED) {
            showNotification("Session expired — please save your work and log in again.", "warning", 6000);
            return;
        }
        extendSessionOnAction?.();

        addMessage(uploadMode === "file" ? "Upload started" : "Text processing started");

        // Permission check
        if (session?.shareInfo && !canPerformAction(session.shareInfo, "add")) {
            showNotification("You don't have permission to add documents", "error");
            return;
        }

        // Use session's document type (from dropdown UI) instead of dropdownState's default
        const { patientData, patientDocuments } = dropdownState;
        const selectedDocumentType = session?.selectedDocumentType || dropdownState?.selectedDocumentType;
        const selectedDocumentTypeId = session?.selectedDocumentTypeId || dropdownState?.selectedDocumentTypeId;
        const selectedCategoryId = session?.selectedCategoryId || dropdownState?.selectedCategoryId;
        const documentTypes = session?.documentTypes || dropdownState?.documentTypes;
        const documentType = documentTypes?.find((t) => t.id === selectedDocumentTypeId);
        const documentSections = documentType?.sections || null;

        // Require a real document type before submitting — see requestStart in
        // useRecordingSession.js for the same guard + rationale.
        if (!selectedDocumentTypeId) {
            showNotification("Please select a document type before uploading.", "error");
            return;
        }

        const fileMimeType = uploadMode === "file" ? getUploadMimeType(selectedFile) : null;
        if (uploadMode === "file" && !fileMimeType) {
            showNotification("Please select a PDF or image file.", "error");
            return;
        }

        setIsProcessing(true);

        // 1. Create Firestore document
        const initialData = {
            ...createInitialDocumentStructure(
                uploadMode === "file" ? "upload" : "text",
                selectedDocumentType, selectedDocumentTypeId, selectedCategoryId,
                Date.now(), documentSections
            ),
            ...getSharedDocumentMetadata(userId, session?.shareInfo),
        };
        if (uploadMode === "text") {
            initialData.processed_text = { summary: uploadTextContent, humanReview: false };
        }

        let newDocId;
        try {
            newDocId = await addData(`patients/${patientId}/documents`, initialData);
            onUploadDocCreated?.(newDocId);
            const uploadName = `${patientData?.firstName || ""} ${patientData?.lastName || ""}`.trim();
            const uploadLabel = uploadMode === "text" ? "Text Upload" : fileMimeType === "application/pdf" ? "PDF Upload" : "Image Upload";
            addPendingJob(newDocId, uploadLabel, patientId, uploadName);
            logAuditEventAsync(AuditActions.DOCUMENT_CREATE, {
                resourceId: newDocId, patientId,
                metadata: {
                    typeId: selectedDocumentTypeId,
                    typeName: documentType?.name || selectedDocumentType,
                    analyticsCategory: documentType?.subcategory || null,
                    source: uploadMode === "file" ? "file-upload" : "text-upload",
                },
            });
        } catch (err) {
            console.error("[useUploadFlow] Failed to create upload document:", err);
            showNotification("Failed to create document", "error");
            setIsProcessing(false);
            return;
        }

        // 2. OCR extraction (file mode only)
        let textToProcess = uploadTextContent;
        if (uploadMode === "file") {
            setUploadStatus("extracting");
            try {
                const authHeaders = await getAuthHeaders();
                // Request a signed GCS upload URL so the file bypasses App Engine's
                // 32MB request-size cap (see OCR large-file boundary triage).
                const uploadUrlResponse = await withRetry(
                    () =>
                        fetch(`${ServerURI}/api/v3/ocr/upload-url`, {
                            method: "POST",
                            headers: authHeaders,
                            body: JSON.stringify({
                                fileName: selectedFile.name,
                                mimeType: fileMimeType,
                                patientId,
                                docId: newDocId,
                            }),
                        }),
                    3
                );

                if (!uploadUrlResponse.ok) {
                    const uploadUrlError = await uploadUrlResponse.json().catch(() => ({}));
                    throw new Error(uploadUrlError.error || "Could not prepare the file upload. Please try again.");
                }

                const { signedUrl, gcsUri } = await uploadUrlResponse.json();
                if (!signedUrl || !gcsUri) {
                    throw new Error("Could not prepare the file upload. Please try again.");
                }

                const gcsUploadResponse = await withRetry(
                    () =>
                        fetch(signedUrl, {
                            method: "PUT",
                            headers: { "Content-Type": fileMimeType },
                            body: selectedFile,
                        }),
                    3
                );

                if (!gcsUploadResponse.ok) {
                    throw new Error("The file could not be uploaded. Please try again.");
                }

                const ocrResponse = await withRetry(() =>
                    fetch(`${ServerURI}/api/v3/ocr`, {
                        method: "POST",
                        headers: {
                            ...authHeaders,
                            "X-JSON-Fields": JSON.stringify({
                                docTypeId: selectedDocumentTypeId,
                                patientData,
                                patientId,
                                docId: newDocId,
                                gcsUri,
                                mimeType: fileMimeType,
                            }),
                            "X-Request-Timeout": "120000",
                        },
                    }), 3);

                if (!ocrResponse.ok) {
                    const ocrError = await ocrResponse.json().catch(() => ({}));
                    const details = [ocrResponse.status, ocrError.userMessage || ocrError.error || "OCR extraction failed"].filter(Boolean).join(" - ");
                    throw new Error(details);
                }
                const ocrData = await ocrResponse.json();

                // Batch OCR (202) — backend processes async and saves to Firestore
                if (ocrResponse.status === 202) {
                    addMessage("Large document detected — processing in background...");
                    const POLL_INTERVAL = 5000;
                    const MAX_POLL_TIME = 10 * 60 * 1000; // 10 minutes
                    const startPoll = Date.now();

                    while (Date.now() - startPoll < MAX_POLL_TIME) {
                        await new Promise(r => setTimeout(r, POLL_INTERVAL));
                        const docData = await getData(`patients/${patientId}/documents/${newDocId}`);
                        if (docData?.status === "error") {
                            throw new Error("OCR extraction failed for large document");
                        }
                        if (docData?.textExtractionComplete && docData?.processed_text?.summary) {
                            textToProcess = docData.processed_text.summary;
                            break;
                        }
                    }
                    if (!textToProcess || textToProcess.length < minimumTextLength) {
                        throw new Error("Text extraction timed out or returned insufficient text — try a smaller document.");
                    }
                } else {
                    // Sync OCR (200) — text returned in response
                    textToProcess = ocrData.text;
                    if (!textToProcess || textToProcess.length < minimumTextLength) {
                        throw new Error("Extracted text too short — try pasting text directly instead.");
                    }
                    await updateData(`patients/${patientId}/documents`, newDocId, {
                        processed_text: { summary: textToProcess, humanReview: false },
                        modifiedAt: new Date(),
                        textExtractionComplete: true,
                    });
                }
            } catch (err) {
                console.error("[useUploadFlow] OCR extraction failed:", err);
                showNotification(err.message || "Text extraction failed", "error");
                removePendingJob(newDocId);
                await updateData(`patients/${patientId}/documents`, newDocId, {
                    status: "error", modifiedAt: new Date(),
                });
                setIsProcessing(false);
                setUploadStatus("error");
                return;
            }
        }

        // 3. Submit for v3 processing
        setUploadStatus("processing");
        const result = await submitForProcessing({
            text: textToProcess,
            documentTypeId: selectedDocumentTypeId,
            patientData,
            patientDocuments,
            documentId: newDocId,
            patientId,
            source: uploadMode === "file" ? "upload" : "text",
        });

        if (!result.success) {
            showNotification(result.error || "Processing failed", "error");
            removePendingJob(newDocId);
            setUploadStatus("error");
            setIsProcessing(false);
        } else {
            // Upload successfully submitted — notify caller and reset UI
            setShowUploadUI(false);
            setSelectedFile(null);
            setUploadTextContent("");
            onUploadComplete?.();
        }
    }, [uploadMode, selectedFile, uploadTextContent, patientId, userId, dropdownState, session, setIsProcessing, onUploadDocCreated, addMessage, sessionState, extendSessionOnAction, onUploadComplete, addPendingJob, removePendingJob, setUploadStatus]);

    const handleFileSelect = useCallback((file) => {
        if (!file) return;
        if (!getUploadMimeType(file)) {
            showNotification("Please select a PDF or image file.", "error");
            return;
        }
        setSelectedFile(file);
        addMessage(`File selected: ${file.name}`);
    }, [addMessage]);

    return {
        showUploadUI,
        setShowUploadUI,
        uploadMode,
        setUploadMode,
        selectedFile,
        setSelectedFile,
        uploadTextContent,
        setUploadTextContent,
        fileInputRef,
        handleCancelUpload,
        handleBeginUpload,
        handleFileSelect,
    };
}
